import { lazy, Suspense, useId, useRef, useState, type ReactNode } from 'react';
import type { RouteLinkProps } from './layout/route-link';

/** The panel's words; English where an app passes none (the portal and the console open in English, O-8). */
export interface UserMenuLabels {
  theme: string;
  system: string;
  light: string;
  dark: string;
  signOut: string;
  /** DS-11b2: the Help item's name. */
  help: string;
}

/**
 * What Help says — DS-11b2 (WCAG 3.2.6; owner, 2026-10-07): who to ask (the app's own sentence, in the chrome's
 * language), whether the app has the palette's Ctrl K, and the installation's support contact if one is set.
 */
export interface HelpContent {
  ask: string;
  palette?: boolean;
  /**
   * The support contact, when the app has it in hand. Left out (every app, 2026-10-08), Help asks the server for the
   * installation's as it opens (`GET /api/installation`, inside Help's lazy chunk) and shows it only when one is set.
   */
  support?: string;
  /**
   * DS-11b2 review: the skip link's own name, as the page shows it ("Skip to content" where none is passed). Help tells
   * the reader to find that link by name, so it must be the name the first Tab reaches: in Bangla chrome, মূল অংশে যান.
   */
  skip?: string;
}

/** DS-11b4 (F600): one release's notes, newest first (`whats-new.ts`, the back office's). */
export interface NewsEntry {
  version: string;
  /** The release's day, ISO (`2026-10-07`). */
  date: string;
  lines: readonly string[];
}

/**
 * DS-11b4 (F600): What's new, as the app passes it — the words, whether the newest notes are unread in this browser
 * (`fresh`: a dot on the button), and `open`, which loads the notes and marks them read. The notes come as a promise so
 * the menu never imports them: they are the back office's, and this file is on the portal's and the console's loads.
 */
export interface NewsContent {
  label: string;
  freshLabel: string;
  fresh: boolean;
  open: () => Promise<readonly NewsEntry[]>;
}

export interface UserMenuProps {
  /** What the button is: "Account" — its whole text where the app has no name to show (the back office). */
  label: string;
  /** The person's name, shown on the button after `label` read only to a screen reader (portal, console). */
  who?: string;
  /** Every word as a prop, never `t()` from here: i18n.ts must not reach the portal or the console (plan C1). */
  labels?: Partial<UserMenuLabels>;
  /** Places to go (the back office: All companies, Summary, Settings), drawn with the app's link (RouteLinkContext). */
  links?: ReadonlyArray<{ label: string; to: RouteLinkProps['to'] }>;
  /** DS-11b2: Help, after the links and before Sign out, in every app (its body is a lazy chunk, UserMenuHelp.tsx). */
  help?: HelpContent;
  /** DS-11b4: What's new, after the links and before Help (the back office; `NewsContent`). */
  news?: NewsContent;
  /**
   * DS-11b4: drawn on the button after its name — the back office's What's new dot, with its word for a screen reader.
   * The app draws it, so the portal's and the console's first loads carry one prop, not the dot (the console's ceiling
   * had 55 bytes to spare with the dot drawn here).
   */
  mark?: ReactNode;
  /** The chrome's language, said on the menu (the back office; WCAG 3.1.2). */
  lang?: string;
  onSignOut: () => unknown;
}

// Loaded when first opened: the theme, the links and Sign out are a click away, and nobody needs them to draw the page.
const Panel = lazy(() => import('./UserMenuPanel').then((m) => ({ default: m.UserMenuPanel })));

/**
 * 🔴 ONE USER MENU — design-system DS-11a3 (§1.5; owner, 2026-10-07: Sign out, All companies, Summary, Settings and
 * Theme in one menu). The button is on every first load, in all three shells; the panel is lazy (UserMenuPanel.tsx).
 *
 * A DISCLOSURE, not `role="menu"`: the panel holds a radio group, links and a button, which a menu's arrow-key model
 * cannot hold, so the button says only whether it is open (`aria-expanded`) and what it opens (`aria-controls`), and Tab
 * walks into the panel, which comes next in the page. Escape, a press outside, or Tab out closes it; Escape and the
 * press give focus back to the button (the panel's own handlers).
 */
export function UserMenu(props: UserMenuProps) {
  const { label, who, lang, mark } = props;
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  return (
    <div className="wb-user" lang={lang}>
      <button
        ref={button}
        type="button"
        className="wb-user__button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(!open)}
      >
        {/* The space between is its own text node: a space at the end of the hidden span is dropped from the name. */}
        {who ? <span className="wb-sr-only">{label}:</span> : null}
        {who ? ' ' : null}
        <span className="wb-user__name">{who || label}</span>
        <span className="wb-user__caret" aria-hidden="true">
          ▾
        </span>
        {mark}
      </button>
      {open ? (
        <Suspense fallback={null}>
          <Panel
            {...props}
            id={id}
            close={(back) => {
              setOpen(false);
              if (back) button.current?.focus();
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
